import { useEffect, useMemo, useState } from "react";
import SearchInput from "./shared/SearchInput";
import { fetchVevoDetail } from "../utils/api";
import { cssUrl } from "../utils/cssVersion";

const filterTemplateStyles = cssUrl("filterTemplate.css");

/* ============================================================================
 * VEVOInfo
 * ----------------------------------------------------------------------------
 * Scans the stored Home Affairs visa grant letters (backend, on load) and
 * shows Current / 3 Month Prior / Expired / Unknown buckets from "Stay until".
 * Non-sensitive fields drive the table; the sensitive detail (grant number,
 * passport, etc.) is only released after a server-side PIN gate. Three wrong
 * PINs logs the admin out.
 *
 * Props:
 *   records         – full array from fetchManagedUsers
 *   statuses        – { [recordId]: {visaType, visaExpiry, dateGranted, dob, scanStatus} }
 *   loading         – bool, scan in flight
 *   onRefresh       – () => void  — force a re-scan
 *   onSelect        – (record) => void  — "Manage User" (close all, panels closed)
 *   onSelectContract– (record) => void  — Unknown row → open Contract Info
 *   onLoggedOut     – () => void  — 3 wrong PINs → route to /index (logout)
 *   onClose         – () => void
 * ==========================================================================*/

const BUCKETS = [
  { key: "current", label: "Current",        tone: "green" },
  { key: "prior",   label: "3 Month Prior",  tone: "amber" },
  { key: "expired", label: "Expired",        tone: "red" },
  { key: "unknown", label: "Unknown",        tone: "slate" },
];
const BUTTON_COLS = 4;

const MONTHS = { january:0, february:1, march:2, april:3, may:4, june:5, july:6,
  august:7, september:8, october:9, november:10, december:11 };

const parseVevoDate = (v) => {
  const s = (v ?? "").toString().trim();
  if (!s) return null;
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = s.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);
  if (m && MONTHS[m[2].toLowerCase()] !== undefined) return new Date(+m[3], MONTHS[m[2].toLowerCase()], +m[1]);
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  return null;
};

const displayDate = (v) => {
  const d = parseVevoDate(v);
  if (!d) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const startOfToday = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const addMonths = (date, n) => { const d = new Date(date.getTime()); d.setMonth(d.getMonth() + n); return d; };

// classify a scanned record into a bucket
// forcedBucket handles permanent visas (always current) and VEVO-check live
// status (In effect -> current, Ceased -> expired); temp visas bucket by date.
const bucketOf = (st, today, soonCutoff) => {
  if (!st) return "unknown";
  if (st.scanStatus === "unknown" || st.scanStatus === "error") return "unknown";
  if (st.forcedBucket === "current") return "current";
  if (st.forcedBucket === "expired") return "expired";
  const exp = parseVevoDate(st.visaExpiry);
  if (!exp) return "unknown";
  if (exp < today) return "expired";
  if (exp <= soonCutoff) return "prior";
  return "current";
};

// first visa attachment URL from a record's serialized "visa" field
const firstVisaUrl = (record) => {
  try {
    const arr = JSON.parse(record.visa || "[]");
    return Array.isArray(arr) && arr[0] ? arr[0].url : null;
  } catch { return null; }
};

// every visa attachment URL for a record (for the paginated detail)
const allVisaUrls = (record) => {
  try {
    const arr = JSON.parse(record.visa || "[]");
    return (Array.isArray(arr) ? arr : []).map((a) => a && a.url).filter(Boolean);
  } catch { return []; }
};

const DOC_KIND_LABEL = {
  grant: "Visa grant letter",
  vevo: "VEVO check",
  nomination: "Nomination approval (sponsorship)",
};

const toneColor = (tone) =>
  tone === "red" ? "#f87171" : tone === "green" ? "#4ade80" : tone === "amber" ? "#fbbf24" : "#94a3b8";

/* ---------- styles (shared language with ABNLookup) ---------- */
const S = {
  backdrop: { position:"fixed", inset:0, top: "var(--fr-modal-top, 0px)", background:"rgba(2,6,23,0.7)", zIndex:1100,
    display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"40px 16px", overflowY:"auto" },
  modal: { position:"relative", maxHeight: "calc(var(--fr-modal-max, 82vh) - 76px)", overflowY: "auto", overscrollBehavior: "contain", width:"100%", maxWidth:880,
    background:"linear-gradient(135deg,#1e293b 0%,#0f172a 100%)",
    border:"1px solid rgba(34,211,238,0.25)", borderRadius:16, padding:"18px 18px 14px" },
  panelAccent: { position:"absolute", top:0, left:0, right:0, height:3,
    background:"linear-gradient(90deg,#22d3ee,#3b82f6,#8b5cf6)" },
  head: { position: "sticky", top: 0, zIndex: 5, background: "#1e293b", boxShadow: "0 -18px 0 0 #1e293b",  display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 },
  title: { margin:0, fontSize:"1rem", fontWeight:700, color:"#f1f5f9" },
  closeX: { background:"none", border:"none", color:"rgba(255,255,255,0.6)", fontSize:"1.4rem", lineHeight:1, cursor:"pointer" },
  eyebrow: { fontSize:"0.62rem", letterSpacing:"0.14em", textTransform:"uppercase", color:"#22d3ee", fontWeight:700 },
  buttonsGrid: { display:"grid", gridTemplateColumns:`repeat(${BUTTON_COLS},minmax(0,1fr))`, gap:10, marginBottom:8 },
  countBtn: (active) => ({ borderRadius:12, padding:"12px 10px", cursor:"pointer",
    background: active ? "rgba(34,211,238,0.15)" : "rgba(34,211,238,0.08)",
    border:`1px solid ${active ? "rgba(34,211,238,0.6)" : "rgba(34,211,238,0.25)"}`,
    display:"flex", flexDirection:"column", alignItems:"center", gap:4 }),
  countLabel: { fontSize:"0.68rem", color:"rgba(255,255,255,0.55)", textTransform:"uppercase",
    letterSpacing:"0.05em", fontWeight:700, textAlign:"center", lineHeight:1.3 },
  countValue: (tone) => ({ fontSize:"1.7rem", fontWeight:800, lineHeight:1, color: toneColor(tone) }),
  card: { background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:"12px", marginTop:14 },
  colHead: { fontSize:"0.62rem", letterSpacing:"0.05em", textTransform:"uppercase", color:"rgba(255,255,255,0.45)",
    whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  rowName: { fontSize:"0.85rem", fontWeight:700, color:"#22d3ee", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  cell: { fontSize:"0.84rem", color:"#e2e8f0", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  empty: { padding:"24px", textAlign:"center", color:"rgba(255,255,255,0.5)", fontStyle:"italic", fontSize:"0.85rem" },
  hint: { marginTop:10, fontSize:"0.72rem", color:"rgba(255,255,255,0.4)", textAlign:"center" },
  closeBtn: { width:"100%", padding:"11px", borderRadius:10, border:"none",
    background:"rgba(255,255,255,0.06)", color:"#e2e8f0", fontWeight:600, cursor:"pointer", marginTop:12 },
  refreshBtn: (busy) => ({ display:"inline-flex", alignItems:"center", gap:6, padding:"6px 12px", borderRadius:999,
    border:"1px solid rgba(34,211,238,0.4)", background:"rgba(34,211,238,0.1)", color:"#22d3ee",
    fontSize:"0.72rem", fontWeight:700, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }),
};
const TABLE_COLS = "1.4fr 1fr 1.1fr 1fr 1fr";

/* ---------- nested overlay styles (PIN + detail) ---------- */
const O = {
  overlay: { position:"fixed", inset:0, top: "var(--fr-modal-top, 0px)", background:"rgba(0,0,0,0.75)", backdropFilter:"blur(6px)",
    display:"flex", alignItems:"center", justifyContent:"center", zIndex:1200, padding:20 },
  modal: { background:"linear-gradient(135deg,#0f172a 0%,#1e293b 100%)", border:"1px solid rgba(34,211,238,0.25)",
    borderRadius:16, width:"100%", maxWidth:520, overflow:"hidden", boxShadow:"0 25px 80px rgba(0,0,0,0.6)" },
  bar: { height:3, background:"linear-gradient(90deg,#22d3ee,#3b82f6,#8b5cf6)" },
  header: { padding:"18px 24px 8px" },
  hTitle: { fontSize:"1.05rem", fontWeight:700, color:"#f1f5f9", margin:0 },
  body: { padding:"12px 24px 4px", color:"rgba(255,255,255,0.8)", fontSize:"0.88rem" },
  footer: { padding:"16px 24px", display:"flex", justifyContent:"flex-end", gap:10,
    background:"rgba(0,0,0,0.2)", borderTop:"1px solid rgba(255,255,255,0.06)" },
  cancel: { background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.7)", border:"1px solid rgba(255,255,255,0.1)",
    borderRadius:10, padding:"10px 18px", fontSize:"0.85rem", fontWeight:600, cursor:"pointer" },
  confirm: { background:"linear-gradient(135deg,#22d3ee 0%,#3b82f6 100%)", color:"#0f172a", border:"none",
    borderRadius:10, padding:"10px 22px", fontSize:"0.85rem", fontWeight:700, cursor:"pointer" },
  pinInput: { width:"100%", marginTop:12, padding:"12px 14px", borderRadius:10, background:"rgba(255,255,255,0.04)",
    border:"1px solid rgba(34,211,238,0.3)", color:"#f1f5f9", fontSize:"1.4rem", letterSpacing:"0.5em",
    textAlign:"center", outline:"none" },
  err: { marginTop:10, color:"#fca5a5", fontSize:"0.8rem", textAlign:"center" },
  detailRow: { display:"grid", gridTemplateColumns:"150px 1fr", gap:10, padding:"8px 4px",
    borderBottom:"1px solid rgba(255,255,255,0.06)" },
  dLabel: { fontSize:"0.72rem", textTransform:"uppercase", letterSpacing:"0.04em", color:"rgba(255,255,255,0.5)", fontWeight:700 },
  dValue: { fontSize:"0.9rem", color:"#e2e8f0", wordBreak:"break-word" },
  pager: { width:28, height:28, borderRadius:8, border:"1px solid rgba(34,211,238,0.4)",
    background:"rgba(34,211,238,0.1)", color:"#22d3ee", cursor:"pointer", fontSize:"0.8rem", lineHeight:1 },
  note: { padding:"8px 12px", borderRadius:8, marginBottom:8, fontSize:"0.78rem",
    background:"rgba(251,191,36,0.08)", border:"1px solid rgba(251,191,36,0.3)", color:"#fbbf24", lineHeight:1.4 },
};

/* ---------- PIN gate modal ---------- */
const PinModal = ({ record, visaUrls, onUnlocked, onLoggedOut, onClose }) => {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy || pin.length < 4) return;
    setBusy(true); setErr("");
    const res = await fetchVevoDetail({ recordId: record.recordId, visaUrls, pin });
    setBusy(false);
    if (res.loggedOut) { onLoggedOut(); return; }
    if (res.status === "success") { onUnlocked(res.details || []); return; }
    setPin("");
    setErr(res.attemptsLeft != null ? `${res.error}. ${res.attemptsLeft} attempt(s) left.` : (res.error || "Incorrect PIN"));
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }); // eslint-disable-line

  return (
    <div style={O.overlay} onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...O.modal, maxWidth: 400 }}>
        <div style={O.bar} />
        <div style={O.header}><h5 style={O.hTitle}>🔒 Enter access PIN</h5></div>
        <div style={O.body}>
          Viewing sensitive visa details for <strong style={{ color:"#22d3ee" }}>{record.name}</strong>.
          Enter the 6-digit PIN. <span style={{ color:"#fca5a5" }}>3 wrong attempts will log you out.</span>
          <input
            type="password" inputMode="numeric" autoFocus maxLength={6} value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            style={O.pinInput} placeholder="••••••"
          />
          {err && <div style={O.err}>{err}</div>}
        </div>
        <div style={O.footer}>
          <button type="button" style={O.cancel} onClick={onClose}>Cancel</button>
          <button type="button" style={O.confirm} onClick={submit} disabled={busy || pin.length < 4}>
            {busy ? "Checking…" : "Unlock"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------- read-only sensitive detail modal — paginated across all docs ---- */
const DetailModal = ({ record, details, onManageUser, onClose }) => {
  const list = Array.isArray(details) && details.length ? details : [{ scanStatus: "unknown" }];
  const [idx, setIdx] = useState(0);
  const safe = Math.min(idx, list.length - 1);
  const d = list[safe];
  const kind = d?.docKind;

  const row = (label, value) => (
    <div style={O.detailRow}>
      <span style={O.dLabel}>{label}</span>
      <span style={O.dValue}>{value || "—"}</span>
    </div>
  );
  const conds = Array.isArray(d?.conditions) ? d.conditions : [];

  return (
    <div style={O.overlay} onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...O.modal, maxWidth: 560 }}>
        <div style={O.bar} />
        <div style={O.header}>
          <h5 style={O.hTitle}>Visa details — {record.name}</h5>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:8 }}>
            <span style={{ fontSize:"0.72rem", textTransform:"uppercase", letterSpacing:"0.05em", color:"#22d3ee", fontWeight:700 }}>
              {DOC_KIND_LABEL[kind] || "Document"}
            </span>
            {list.length > 1 && (
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <button type="button" style={O.pager} disabled={safe === 0} onClick={() => setIdx(safe - 1)}>◄</button>
                <span style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.6)" }}>Doc {safe + 1} of {list.length}</span>
                <button type="button" style={O.pager} disabled={safe === list.length - 1} onClick={() => setIdx(safe + 1)}>►</button>
              </div>
            )}
          </div>
        </div>

        <div style={{ ...O.body, maxHeight: "min(60vh, calc(100dvh - var(--fr-modal-top, 0px) - 200px))", overflowY: "auto" }}>
          {kind === "nomination" ? (
            <>
              <div style={O.note}>Sponsorship nomination approval.</div>
              {row("Visa Program", d?.visaType)}
              {row("Nomination TRN", d?.nominationTRN)}
              {row("Nomination Application ID", d?.applicationId)}
              {row("Nominee", d?.nomineeName)}
            </>
          ) : kind === "vevo" ? (
            <>
              {row("Visa Type", d?.visaType)}
              {row("Visa Expiry", d?.visaExpiryRaw || displayDate(d?.visaExpiry))}
              {row("Live Visa Status", d?.liveStatus)}
              {row("Date Granted", d?.dateGranted)}
              {row("Date of Birth", d?.dob)}
              {row("Document Number", d?.documentNumber)}
              {row("Country of Document", d?.documentCountry)}
              {row("Visa Stream", d?.visaStream)}
              {row("Work Entitlement", d?.workEntitlement)}
              {row("Period of Stay", d?.periodOfStay)}
              {row("Entries Allowed", d?.entriesAllowed)}
              {row("Location", d?.location)}
            </>
          ) : kind === "grant" ? (
            <>
              {row("Visa Type", d?.visaType)}
              {row("Visa Expiry / Status", d?.stayUntil ? displayDate(d.stayUntil) : (d?.permanent ? "Permanent" : "—"))}
              {row("Date Granted", d?.dateGranted)}
              {row("Date of Birth", d?.dob)}
              {row("Visa Grant Number", d?.grantNumber)}
              {row("Passport Number", d?.passportNumber)}
              {row("Passport Country", d?.passportCountry)}
              {row("Application ID", d?.applicationId)}
              {row("Transaction Ref", d?.transactionRef)}
              {row("Work Limited (8105)", d?.workLimited == null ? "—" : (d.workLimited ? "Yes" : "No"))}
              <div style={{ ...O.detailRow, gridTemplateColumns: "1fr", borderBottom: "none" }}>
                <span style={O.dLabel}>Visa Conditions</span>
                <div style={{ marginTop: 4 }}>
                  {conds.length === 0
                    ? <span style={O.dValue}>—</span>
                    : conds.map((c) => (
                        <div key={c.code} style={{ fontSize:"0.82rem", color:"#e2e8f0", padding:"2px 0" }}>
                          <strong style={{ color:"#22d3ee" }}>{c.code}</strong> — {c.desc}
                        </div>
                      ))}
                </div>
              </div>
            </>
          ) : (
            <div style={O.note}>This document couldn’t be read automatically (image or unrecognised format). Open it in Contract Info to view it.</div>
          )}
        </div>

        <div style={O.footer}>
          <button type="button" style={O.cancel} onClick={onClose}>Cancel</button>
          <button type="button" style={O.confirm} onClick={() => onManageUser(record)}>Manage User</button>
        </div>
      </div>
    </div>
  );
};

/* ---------- main component ---------- */
const VEVOInfo = ({ records, statuses = {}, loading = false, onRefresh, onSelect, onSelectContract, onLoggedOut, onClose }) => {
  const [selection, setSelection] = useState(null);
  const [nameQuery, setNameQuery] = useState("");
  const [pinFor, setPinFor] = useState(null);              // record awaiting PIN
  const [detailFor, setDetailFor] = useState(null);        // { record, detail }

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet"; link.href = filterTemplateStyles;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !pinFor && !detailFor) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, pinFor, detailFor]);

  // scope: Working + Active users who have a visa document
  const scoped = useMemo(
  () => (records || []).filter((r) => statuses[r.recordId]),
  [records, statuses]
  );

  const buckets = useMemo(() => {
    const today = startOfToday();
    const soon = addMonths(today, 3);
    const out = { current: [], prior: [], expired: [], unknown: [] };
    scoped.forEach((r) => { out[bucketOf(statuses[r.recordId], today, soon)].push(r); });
    return out;
  }, [scoped, statuses]);

  const activeBucket = selection ? BUCKETS.find((b) => b.key === selection) : null;
  const bucketRows = selection ? buckets[selection] : [];

  const rows = useMemo(() => {
    const q = nameQuery.toLowerCase().replace(/\s+/g, "");
    if (!q) return bucketRows;
    return bucketRows.filter((r) => (r.name ?? "").toLowerCase().replace(/\s+/g, "").includes(q));
  }, [bucketRows, nameQuery]);

  const toggle = (key) => setSelection((p) => (p === key ? null : key));

  const onRowClick = (record) => {
    if (selection === "unknown") { onSelectContract(record); return; }  // no PIN
    setPinFor(record);                                                  // gate
  };

  const st = (r) => statuses[r.recordId] || {};

  return (
    <div style={S.backdrop} onClick={onClose}>
      <style>{`.vv-row{transition:background .12s ease}.vv-row:hover{background:rgba(34,211,238,0.07)}`}</style>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.panelAccent} />
        <div style={S.head}>
          <h4 style={S.title}>VEVO Info</h4>
          <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, marginBottom:8 }}>
          <div style={S.eyebrow}>Visa expiry</div>
          {onRefresh && (
            <button type="button" onClick={() => !loading && onRefresh()} disabled={loading}
              style={S.refreshBtn(loading)} title="Re-scan every visa document">
              ↻ {loading ? "Scanning…" : "Re-scan"}
            </button>
          )}
        </div>

        <div style={S.buttonsGrid}>
          {BUCKETS.map((b) => (
            <button key={b.key} type="button" onClick={() => toggle(b.key)} style={S.countBtn(selection === b.key)}
              title={`${b.label} visas`}>
              <span style={S.countLabel}>{b.label}</span>
              <span style={S.countValue(b.tone)}>{loading ? "…" : buckets[b.key].length}</span>
            </button>
          ))}
        </div>

        <div style={{ margin:"8px 0 4px" }}>
          <SearchInput value={nameQuery} onChange={setNameQuery} placeholder="Search name in results…" />
        </div>

        {activeBucket && (
          <div style={S.card}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:10 }}>
              <span style={{ ...S.eyebrow, color: toneColor(activeBucket.tone) }}>{activeBucket.label}</span>
              <span style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.45)" }}>
                {nameQuery ? `${rows.length} of ${bucketRows.length}` : rows.length} record{rows.length === 1 ? "" : "s"}
              </span>
            </div>

            {rows.length === 0 ? (
              <div style={S.empty}>{nameQuery ? `No names matching “${nameQuery}”.` : "No matching records."}</div>
            ) : (
              <div style={{ maxHeight:320, overflowY:"auto", overflowX:"auto" }}>
                <div style={{ minWidth:640 }}>
                  <div style={{ display:"grid", gridTemplateColumns:TABLE_COLS, padding:"8px 8px 10px",
                    borderBottom:"1px solid rgba(255,255,255,0.12)", columnGap:8, position:"sticky", top:0, background:"#1b2537", zIndex:1 }}>
                    <span style={S.colHead}>Name</span>
                    <span style={S.colHead}>Visa Expiry</span>
                    <span style={S.colHead}>Visa Type</span>
                    <span style={S.colHead}>Date Granted</span>
                    <span style={S.colHead}>DOB</span>
                  </div>
                  {rows.map((r, i) => (
                    <div key={r.recordId || i} className="vv-row" onClick={() => onRowClick(r)}
                      style={{ display:"grid", gridTemplateColumns:TABLE_COLS, alignItems:"center", padding:"10px 8px",
                        cursor:"pointer", borderRadius:8, borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none", columnGap:8 }}
                      title={selection === "unknown" ? "Open in Contract Info" : "Enter PIN to view visa details"}>
                      <span style={S.rowName}>
                        {r.name || "—"}
                        {selection !== "unknown" && st(r).hasNomination && (
                          <span style={{ marginLeft:6, fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.04em",
                            padding:"1px 6px", borderRadius:999, verticalAlign:"middle",
                            background:"rgba(168,85,247,0.15)", color:"#c084fc", border:"1px solid rgba(168,85,247,0.4)" }}>
                            SPONSORED
                          </span>
                        )}
                      </span>
                      <span style={S.cell}>{selection === "unknown" ? "—" : (st(r).visaExpiry ? displayDate(st(r).visaExpiry) : (st(r).expiryLabel || "—"))}</span>
                      <span style={S.cell}>{selection === "unknown" ? "—" : (st(r).visaType || "—")}</span>
                      <span style={S.cell}>{selection === "unknown" ? "—" : (st(r).dateGranted || "—")}</span>
                      <span style={S.cell}>{selection === "unknown" ? "—" : (st(r).dob || "—")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={S.hint}>
              {selection === "unknown"
                ? "Document couldn’t be read — click a row to open it in Contract Info."
                : "Click a record and enter the PIN to view sensitive visa details."}
            </div>
          </div>
        )}

        <button type="button" style={S.closeBtn} onClick={onClose}>Close</button>
      </div>

      {pinFor && (
        <PinModal
          record={pinFor}
          visaUrls={allVisaUrls(pinFor)}
          onUnlocked={(details) => { setDetailFor({ record: pinFor, details }); setPinFor(null); }}
          onLoggedOut={onLoggedOut}
          onClose={() => setPinFor(null)}
        />
      )}

      {detailFor && (
        <DetailModal
          record={detailFor.record}
          details={detailFor.details}
          onManageUser={onSelect}
          onClose={() => setDetailFor(null)}
        />
      )}
    </div>
  );
};

export default VEVOInfo;
